import { useEffect, useMemo, useRef, useState } from "react";
import { isAppsInTossChannel } from "../api-url";
import { track } from "../analytics/events";
import {
  canRequestGuideAd,
  publicGuideSurfaceForRoute,
  readGuideAdFrequency,
  recordGuideAdImpression,
  type AdChoice,
  type PublicGuideSurface,
  type StorageLike,
  type WebProviderState,
} from "./guide-ad-policy";

type GuideAdCallbacks = {
  onImpression: () => void;
  onViewable: () => void;
  onClick: () => void;
  onNoFill: () => void;
  onError: () => void;
};
type GuideAdAdapter = {
  mount: (container: HTMLElement, adGroupId: string, callbacks: GuideAdCallbacks) => () => void;
};
type GuideAdRuntime = {
  productionActivated: boolean;
  adGroupId: string | null;
  webProviderState: WebProviderState;
};

// Owner approved this Toss guide-end SDK scope on 2026-10-13.
// Web remains off while AdSense is PREPARING and its adapter is absent.
export const disabledGuideAdAdapter: GuideAdAdapter = { mount: () => () => undefined };
export const guideAdRuntime: GuideAdRuntime = {
  productionActivated: true,
  adGroupId: "ait.v2.live.d9b7a302554446a4",
  webProviderState: "PREPARING",
};

declare global {
  interface Window {
    __jgmMountTossGuideBanner?: GuideAdAdapter["mount"];
  }
}

const emitGuideEvent = (name: string, properties: Record<string,string>) => track(name,properties);

export function GuideEndAdOffer({
  surface,
  choice,
  requestChoice,
  revokeChoice,
  runtime = guideAdRuntime,
  adapter = disabledGuideAdAdapter,
  sessionStore,
  persistentStore,
  emit = emitGuideEvent,
}: {
  surface: PublicGuideSurface;
  /** Product choice only. Provider privacy authorization is separately frozen in runtime. */
  choice: AdChoice;
  requestChoice: () => void;
  revokeChoice: () => void;
  runtime?: GuideAdRuntime;
  adapter?: GuideAdAdapter;
  sessionStore?: StorageLike;
  persistentStore?: StorageLike;
  emit?: (name: string, properties: Record<string, string>) => void;
}) {
  const route = typeof window === "undefined" ? "" : window.location.pathname;
  const channel = isAppsInTossChannel() ? "appsInToss" : "web";
  const {session,persistent} = useMemo(() => {try {return {session:sessionStore ?? (typeof window === "undefined" ? null : window.sessionStorage), persistent:persistentStore ?? (typeof window === "undefined" ? null : window.localStorage)};} catch {return {session:null,persistent:null};}}, [sessionStore,persistentStore]);
  const frequency = useMemo(() => {try {return session && persistent ? readGuideAdFrequency(session, persistent) : { sessionImpressions: 1, lastImpressionAt: null };} catch {return {sessionImpressions:1,lastImpressionAt:null};}}, [session, persistent]);
  const [requested, setRequested] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [slotMounted, setSlotMounted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [revoked, setRevoked] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const eligible = publicGuideSurfaceForRoute(route) === surface;
  const decision = canRequestGuideAd({
    route, channel, surface, userRequested: requested,
    choice: revoked ? "denied" : choice,
    productionActivated: runtime.productionActivated,
    adGroupId: runtime.adGroupId,
    webProviderState: runtime.webProviderState,
    ...frequency,
    now: Date.now(),
  });

  const tossMount = typeof window === "undefined" ? undefined : window.__jgmMountTossGuideBanner;
  const servingAdapter = useMemo<GuideAdAdapter>(() => channel === "appsInToss"
    ? { mount: tossMount ?? adapter.mount }
    : disabledGuideAdAdapter, [channel, tossMount, adapter]);

  useEffect(() => {
    if (!authorized || !eligible || failed || revoked || choice !== "granted" || !runtime.adGroupId || !slotRef.current || !session || !persistent) return;
    emit("guide_ad_request", { surface });
    const providerCleanup = servingAdapter.mount(slotRef.current, runtime.adGroupId, {
      onImpression: () => {
        try {recordGuideAdImpression(session, persistent, Date.now());} catch {setFailed(true);}
        emit("guide_ad_impression", { surface });
      },
      // Never send provider metadata, creative/request IDs, money or user data.
      onViewable: () => undefined,
      onClick: () => undefined,
      onNoFill: () => { setFailed(true); setSlotMounted(false); emit("guide_ad_no_fill", { surface }); },
      onError: () => { setFailed(true); setSlotMounted(false); emit("guide_ad_error", { surface }); },
    });
    let cleaned = false;
    const cleanupOnce = () => {
      if (cleaned) return;
      cleaned = true;
      providerCleanup();
    };
    cleanupRef.current = cleanupOnce;
    setSlotMounted(true);
    return () => {
      if (cleanupRef.current === cleanupOnce) cleanupRef.current = null;
      cleanupOnce();
      setSlotMounted(false);
    };
    // Frequency is checked once before explicit mount. Updating its local cap
    // on impression must not tear down an already-rendered banner.
  }, [authorized, eligible, failed, revoked, choice, runtime.adGroupId, session, persistent, surface, servingAdapter, emit]);

  if (!eligible || failed || !runtime.productionActivated || !runtime.adGroupId) return null;
  if (channel === "appsInToss" && !tossMount && adapter === disabledGuideAdAdapter) return null;
  if (channel === "web") return null;
  if (!requested && (frequency.sessionImpressions >= 1 || (frequency.lastImpressionAt !== null && Date.now() - frequency.lastImpressionAt < 7*24*60*60*1000))) return null;
  if (revoked || choice === "denied") return null;
  if (choice !== "granted") return <section className="guide-ad-choice" aria-label="공개 가이드 광고 설정"><p>공개 가이드를 읽은 뒤에만 선택할 수 있어요. 광고 요청 시 토스 광고 SDK가 동작하며 플랫폼의 개인정보 처리 규칙이 적용돼요. 일본골목은 여행 내용·정확한 위치·사진을 전달하지 않아요.</p><button className="button secondary" type="button" onClick={requestChoice}>가이드 광고 설정</button></section>;
  if (!requested) return <section className="guide-ad-offer" aria-label="공개 가이드 광고"><p>가이드 끝에서 원할 때만 광고를 불러와요. 여행 기능은 그대로 무료예요.</p><button className="button secondary" type="button" onClick={() => {
    const nextDecision = canRequestGuideAd({
      route, channel, surface, userRequested: true, choice,
      productionActivated: runtime.productionActivated, adGroupId: runtime.adGroupId,
      webProviderState: runtime.webProviderState, ...frequency, now: Date.now(),
    });
    if (!nextDecision.allowed) return;
    setRequested(true);
    setAuthorized(true);
    emit("guide_ad_offer_select", { surface });
  }}>가이드 광고 보기</button></section>;
  if (!decision.allowed && !slotMounted) return null;
  return <section className="guide-ad-slot" aria-label="광고"><span className="guide-ad-slot__label">Ad</span><div ref={slotRef} className="guide-ad-slot__unit" style={{ width: "100%", height: 96 }} /><button className="button text" type="button" onClick={() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    setRevoked(true);
    setRequested(false);
    setAuthorized(false);
    setSlotMounted(false);
    revokeChoice();
  }}>광고 그만 보기</button></section>;
}

export function PublicGuideAd({surface}:{surface:PublicGuideSurface}) {
  const [choice,setChoice] = useState<AdChoice>("unknown");
  return <GuideEndAdOffer surface={surface} choice={choice} requestChoice={()=>setChoice("granted")} revokeChoice={()=>setChoice("denied")} />;
}
